/**
 * 事件星云（design §11.11，P4a）的纯逻辑：有照片的日子里，相邻的（只差一天）连成一件事，
 * 两天以上的事罩一片淡雾。这里只管「几天算一件事、那片雾摆哪、多大、什么颜色、镜头多远显多强」，
 * 不碰 three.js，好写单元测试；画的部分在 nebula.ts，什么时候重算在 galaxy.ts。
 */
import { daysBetween, type LocalDate, type Vec3 } from '@star-sea/shared';

/**
 * 输入有照片的日子（可以乱序、可以有重复），输出按时间排好的组：
 * 后一天和前一天只差一天就连进同一件「事」，中间空了就断开。单独一天也算一组——
 * 事件星云（groupEvents）只留两天以上的，漫游挑站（roam.ts 的 pickStops）连单天的也要。
 */
export function groupRuns(days: readonly LocalDate[]): LocalDate[][] {
  const sorted = [...new Set(days)].sort();
  const runs: LocalDate[][] = [];
  let cur: LocalDate[] = [];
  for (const d of sorted) {
    if (cur.length && daysBetween(cur[cur.length - 1]!, d) !== 1) {
      runs.push(cur);
      cur = [];
    }
    cur.push(d);
  }
  if (cur.length) runs.push(cur);
  return runs;
}

/** 同 groupRuns，但只返回两天以上的事——一天的没有雾 */
export function groupEvents(days: readonly LocalDate[]): LocalDate[][] {
  return groupRuns(days).filter((r) => r.length > 1);
}

/** 一片雾：位置、椭圆的长短轴、颜色都由它罩着的那几天和那几天的星决定 */
export interface NebulaPatch {
  /** 首末两天锚点的中点，y 再抬 0.18 */
  x: number;
  y: number;
  z: number;
  /** 长轴在盘面（XZ）上的单位方向 */
  ax: number;
  az: number;
  /** 长半轴、短半轴 */
  major: number;
  minor: number;
  r: number;
  g: number;
  b: number;
  /** 每个实例一个随机相位：噪声和呼吸错开 */
  seed: number;
}

/** 雾的颜色往这个淡蓝紫拉一半 */
const HAZE = { r: 0.45, g: 0.5, b: 0.9 };
/**
 * 目标线性亮度。雾很淡，宁淡勿亮（辉光阈值 1.2，不能让它晕起来）：
 * 归一到这里，着色器里再呼吸 ±15%，仍在 0.05–0.10 一带
 */
const LUMINANCE = 0.075;

/** 一件事里照片主色（线性，rgb 三个一数）的平均，往淡蓝紫拉一半，再压到目标亮度 */
export function eventTint(colors: ArrayLike<number>): [number, number, number] {
  const n = Math.floor(colors.length / 3);
  let r = HAZE.r, g = HAZE.g, b = HAZE.b;
  if (n) {
    r = g = b = 0;
    for (let i = 0; i < n * 3; i += 3) {
      r += colors[i]!;
      g += colors[i + 1]!;
      b += colors[i + 2]!;
    }
    r /= n;
    g /= n;
    b /= n;
  }
  r = (r + HAZE.r) / 2;
  g = (g + HAZE.g) / 2;
  b = (b + HAZE.b) / 2;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const k = LUMINANCE / Math.max(lum, 1e-4);
  return [r * k, g * k, b * k];
}

/**
 * 一件事的雾（椭圆）：中心是首末两天锚点的中点、比它们的平均高度再高 0.18；
 * 长轴沿首末两天连线的方向、长是两天距离的一半加 3.2，短轴是这件事里最大一天的星团半径加 2.2
 * （星团向日葵铺开，半径约 0.17·√n）。椭圆平躺在盘面上，不是面向镜头的广告牌。
 */
export function nebulaPatch(first: Vec3, last: Vec3, cluster: number, colors: ArrayLike<number>, seed: number): NebulaPatch {
  const dx = last.x - first.x, dz = last.z - first.z, len = Math.hypot(dx, dz);
  // 首末锚点几乎重合（理论上不会，兜底）时退化成圆，方向随便取一个
  const ax = len > 1e-6 ? dx / len : 1, az = len > 1e-6 ? dz / len : 0;
  const [r, g, b] = eventTint(colors);
  return {
    x: (first.x + last.x) / 2,
    y: (first.y + last.y) / 2 + 0.18,
    z: (first.z + last.z) / 2,
    ax,
    az,
    major: len / 2 + 3.2,
    minor: cluster + 2.2,
    r,
    g,
    b,
    seed,
  };
}

/** 层级之间的过渡带：以边界为中心、半圈距宽（G = 11，同 semantic.ts 的镜头距离单位），两侧平滑 */
const TIER_BLEND = 11 / 2;

/**
 * 镜头离注视点多远时雾有多强（分层的距离和 semantic.ts 一致，各段之间 smoothstep 平滑过渡）：
 * 远于 9·11（总览）×0.5、4·11 到 9·11（一年）×1、14 到 4·11（月和日）×0.35；
 * 近于 14 以后按 semantic 的 DAY_FAR→DAY_NEAR（14→6）淡到 0——看照片时的层级没有雾。
 */
export function nebulaZoom(dist: number): number {
  const ss = (a: number, b: number) => {
    const t = Math.min(1, Math.max(0, (dist - a) / (b - a)));
    return t * t * (3 - 2 * t);
  };
  return (
    (0.5 + 0.5 * ss(9 * 11 + TIER_BLEND, 9 * 11 - TIER_BLEND)) *
    (1 - 0.65 * ss(4 * 11 + TIER_BLEND, 4 * 11 - TIER_BLEND)) *
    (1 - ss(14, 6))
  );
}
